/* ------------------------------------------------------------------------------
 *
 *  # Student Management Components
 *
 *  Student grid and related functionality using DataTables
 *
 * ---------------------------------------------------------------------------- */

import {
	initGenericDataTable,
	refreshDataTable,
	destroyDataTable
} from '../integrations/datatables.js';

// Student Grid Variables
let studentGridCallbacks = null;

// Helper functions to simulate StudentInfo methods
function getFullName(student) {
	return `${student.firstName || ''} ${student.lastName || ''}`.trim();
}

function getInitials(student) {
	const first = student.firstName ? student.firstName[0].toUpperCase() : '';
	const last = student.lastName ? student.lastName[0].toUpperCase() : '';
	return `${first}${last}`;
}

function getStatusBadgeClass(isActive) {
	return isActive ? 'bg-success' : 'bg-secondary';
}

function getStatusText(isActive) {
	return isActive ? 'Activo' : 'Inactivo';
}

// Student-specific DataTable Functions
export function initStudentGrid(students, userRole = 'Padre') {
	destroyDataTable('studentsTable');
	// Prepare data for DataTable
	const tableData = students.map(student => [
		// Actions column (first)
		generateStudentActions(student, userRole),
		generateStudentColumn(student),
		generateAgeColumn(student),
		generateStatusColumn(student),
		student.createdAt ? new Date(student.createdAt).toLocaleDateString('es-ES') : '-'
	]);

	const options = {
		data: tableData,
		columns: [
			{ title: 'Acciones', orderable: false, className: 'text-center no-export', width: '120px' },
			{ title: 'Estudiante', width: '40%' },
			{ title: 'Edad', className: 'text-center', width: '15%' },
			{ title: 'Estado', className: 'text-center', width: '15%' },
			{ title: 'Fecha Registro', width: '20%' }
		],
		exportTitle: 'Estudiantes del Sistema - Montessori Nova School',
		order: [[1, 'asc']], // Order by name
		buttons: [  // Personalización de botones para estudiantes
			{
				extend: 'collection',
				className: 'btn btn-flat-primary btn-labeled btn-labeled-start dropdown-toggle me-3 rounded',
				text: '<span class="btn-labeled-icon bg-primary text-white rounded-start"><i class="ph-export"></i></span>Exportar',
				buttons: [
					{
						extend: 'excel',
						text: '<i class="ph-file-xls me-1"></i>Excel',
						className: 'dropdown-item',
						title: 'Estudiantes del Sistema - Montessori Nova',
						exportOptions: {
							columns: [1, 2, 3, 4],  // Índices específicos para estudiantes (excluye columna 0 de acciones)
							format: {
								body: function (inner, coldex, rowdex) {
									// Manejar casos nulos, undefined o texto plano para evitar errores de jQuery
									if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
										return inner;
									}
									const $el = $(inner);
									const exportText = $el.attr('data-export');
									if (exportText) {
										return exportText;
									}
									// Para otras columnas, elimina HTML como antes
									if (inner.length <= 0) return inner;
									let el = $.parseHTML(inner);
									let result = '';
									$.each(el, function (index, item) {
										if (item.innerText === undefined) {
											result = result + item.textContent;
										} else {
											result = result + item.innerText;
										}
									});
									return result;
								}
							}
						}
					},
					{
						extend: 'copy',
						text: '<i class="ph-clipboard-text me-1"></i>Copiar',
						className: 'dropdown-item',
						exportOptions: {
							columns: [1, 2, 3, 4],  // Índices específicos para estudiantes (excluye columna 0 de acciones)
							format: {
								body: function (inner, coldex, rowdex) {
									// Manejar casos nulos, undefined o texto plano para evitar errores de jQuery
									if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
										return inner;
									}
									const $el = $(inner);
									const exportText = $el.attr('data-export');
									if (exportText) {
										return exportText;
									}
									// Para otras columnas, elimina HTML como antes
									if (inner.length <= 0) return inner;
									let el = $.parseHTML(inner);
									let result = '';
									$.each(el, function (index, item) {
										if (item.innerText === undefined) {
											result = result + item.textContent;
										} else {
											result = result + item.innerText;
										}
									});
									return result;
								}
							}
						}
					}
				]
			}
		]
	};

	return initGenericDataTable('studentsTable', options);
}

function generateStudentActions(student, userRole = 'Padre') {
// Para Padre: solo Ver
if (userRole === 'Padre') {
return `
<div class="d-inline-flex">
<a href="javascript:void(0)" class="text-body" 
   onclick="window.sfBlazorSB.students.editStudent(${student.id})" 
   title="Ver estudiante">
<i class="ph-eye"></i>
</a>
</div>
`;
}

// Para Guía/Admin: Editar + Toggle Status
	const toggleIcon = student.isActive ? 'ph-eye-slash' : 'ph-eye';
	const toggleTitle = student.isActive ? 'Desactivar estudiante' : 'Activar estudiante';
	
	return `
		<div class="d-inline-flex">
			<a href="javascript:void(0)" class="text-body me-2" onclick="window.sfBlazorSB.students.editStudent(${student.id})" title="Ver estudiante">
				<i class="ph-user-circle"></i>
			</a>
			<a href="javascript:void(0)" class="text-body" onclick="window.sfBlazorSB.students.toggleStudentStatus(${student.id}, ${student.isActive})" title="${toggleTitle}">
				<i class="${toggleIcon}"></i>
			</a>
		</div>
	`;
}

function generateStudentColumn(student) {
    const fullName = getFullName(student);
    const initials = getInitials(student);
    const exportText = [initials, fullName].filter(Boolean).join(' - ');
    
    const profileImage = `<div class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style="width: 40px; height: 40px; font-weight: 600;">${initials}</div>`;
    
    return `
        <div class="d-flex align-items-center" data-export="${exportText.replace(/"/g, '&quot;')}" data-date-of-birth="${student.dateOfBirth}" data-ambiente="${student.ambienteNombre || ''}">
            <div class="me-3">${profileImage}</div>
            <div>
                <div class="fw-semibold">${fullName}</div>
            </div>
        </div>
    `;
}

function generateAgeColumn(student) {
	const age = student.currentAge;
	const birthDate = new Date(student.dateOfBirth);
	const formattedBirthDate = birthDate.toLocaleDateString('es-ES');
	const exportText = `${age} años (${formattedBirthDate})`;

	return `
		<div class="text-center" data-export="${exportText}">
			<div class="fw-semibold">${age} años</div>
			<div class="text-muted fs-sm">${formattedBirthDate}</div>
		</div>
	`;
}

function generateStatusColumn(student) {
	const statusBadgeClass = getStatusBadgeClass(student.isActive);
	const statusText = getStatusText(student.isActive);
	return `<span class="badge ${statusBadgeClass}">${statusText}</span>`;
}

// Refresh student grid specifically
export function refreshStudentGrid(students, userRole = 'Padre') {
	destroyDataTable('studentsTable');
	initStudentGrid(students, userRole);
}

// Set callbacks for student grid
export function setStudentCallbacks(dotNetRef) {
	studentGridCallbacks = dotNetRef;
}

// Student action functions
export function editStudent(studentId) {
	if (studentGridCallbacks) {
		studentGridCallbacks.invokeMethodAsync('EditStudent', studentId);
	}
}

export async function toggleStudentStatus(studentId, isActive) {
	const title = isActive ? 'Desactivar Estudiante' : 'Activar Estudiante';
	const text = isActive ? '¿Estás seguro de que deseas desactivar este estudiante?' : '¿Estás seguro de que deseas activar este estudiante?';
	const icon = 'warning';
	const confirmButtonText = isActive ? 'Desactivar' : 'Activar';
	const cancelButtonText = 'Cancelar';
	const confirmButtonColor = isActive ? '#dc3545' : '#28a745';

	try {
		const result = await showConfirmationDialog(title, text, icon, confirmButtonText, cancelButtonText, confirmButtonColor);
		if (result.isConfirmed) {
			if (studentGridCallbacks) {
				await studentGridCallbacks.invokeMethodAsync('ToggleStudentStatus', studentId, isActive);
			}
		}
	} catch (error) {
		console.error('Error toggling student status:', error);
	}
}

// Función genérica para confirmaciones con SweetAlert2
function showConfirmationDialog(title, text, icon, confirmButtonText, cancelButtonText, confirmButtonColor, isHtml) {
	if (isHtml) {
		return window.swalInit.fire({
			title: title,
			html: text,
			icon: icon,
			showCancelButton: true,
			confirmButtonText: confirmButtonText,
			cancelButtonText: cancelButtonText,
			confirmButtonColor: confirmButtonColor || '#dc3545'
		});
	} else {
		return window.swalInit.fire({
			title: title,
			text: text,
			icon: icon,
			showCancelButton: true,
			confirmButtonText: confirmButtonText,
			cancelButtonText: cancelButtonText,
			confirmButtonColor: confirmButtonColor || '#dc3545'
		});
	}
    
}

// Exponer globalmente para reutilización
window.showConfirmationDialog = showConfirmationDialog;

// Apply filters function
export function applyStudentFilters() {
    const birthdayRange = document.getElementById('birthdayRange')?.value || '';
    const ageFilter = parseInt(document.getElementById('ageFilter')?.value) || 0;
    const ambienteFilter = document.getElementById('ambienteFilterStudents')?.value || '';
    const globalSearch = document.getElementById('globalSearch')?.value || '';

    const table = $('#studentsTable').DataTable();

    // Clear previous search functions
    $.fn.dataTable.ext.search.splice(0);
    
    // Add custom search functions
    if (birthdayRange) {
        const [startStr, endStr] = birthdayRange.split(' - ');
        if (startStr && endStr) {
            const startMonth = parseInt(startStr.split('/')[0]) - 1; // 0-based
            const startYear = parseInt(startStr.split('/')[1]);
            const endMonth = parseInt(endStr.split('/')[0]) - 1;
            const endYear = parseInt(endStr.split('/')[1]);
            
            const startDate = new Date(startYear, startMonth, 1);
            const endDate = new Date(endYear, endMonth + 1, 0); // Last day of end month
            
            $.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
                const row = table.row(dataIndex).node();
                const birthDateStr = $(row).find('[data-date-of-birth]').attr('data-date-of-birth');
                if (birthDateStr) {
                    const birthDate = new Date(birthDateStr);
                    const birthMonth = birthDate.getMonth();
                    const birthYear = birthDate.getFullYear();
                    // Check if birth month/year is within the range
                    const birthDateInRange = new Date(birthYear, birthMonth, 15); // Mid month
                    return birthDateInRange >= startDate && birthDateInRange <= endDate;
                }
                return true;
            });
        }
    }

    if (ageFilter > 0) { 
        $.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
            const ageText = data[2]; // Column 2 is age
            const ageMatch = ageText.match(/(\d+)\s*años/);
            const age = ageMatch ? parseInt(ageMatch[1]) : 0;
            return age === ageFilter;
        });
    }

    // Filter by Ambiente
    if (ambienteFilter) {
        $.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
            const row = table.row(dataIndex).node();
            const ambienteNombre = $(row).find('[data-ambiente]').attr('data-ambiente');
            // Match exact ambiente name
            return ambienteNombre && ambienteNombre === ambienteFilter;
        });
    }

    // Apply global search
    table.search(globalSearch).draw();
}

// Clear filters function
export function clearStudentFilters() {
    // Reset form elements
    const birthdayRange = document.getElementById('birthdayRange');
    const ageFilter = document.getElementById('ageFilter');
    const ambienteFilter = document.getElementById('ambienteFilterStudents');
    const globalSearch = document.getElementById('globalSearch');

    if (birthdayRange) birthdayRange.value = '';
    if (ageFilter) ageFilter.value = '0';
    if (ambienteFilter) ambienteFilter.value = '';
    if (globalSearch) globalSearch.value = '';

    const table = $('#studentsTable').DataTable();
    $.fn.dataTable.ext.search.splice(0); // Clear custom searches
    table.search('').draw(); // Clear global search
}

// Initialize birthday picker
export function initBirthdayPicker() {
    if (!$().daterangepicker) {
        console.warn('Warning - daterangepicker.js is not loaded.');
        return;
    }

	let birthdayRange = $('#birthdayRange').daterangepicker({
		parentEl: '.sidebar-secondy',
        showDropdowns: true,
        locale: {
            format: 'MM/YYYY'
		},
		minDate: '01/2000',
		maxDate: '12/2030'
	});

	birthdayRange.val('');
}

// Change age function
export function changeAge(delta) {
    const input = document.getElementById('ageFilter');
    if (input) {
        let val = parseInt(input.value) || 0;
        val += delta;
        if (val < 0) val = 0;
        input.value = val;
    }
}


